import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import moment from "moment";
import MenuItem from "@mui/material/MenuItem";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Autocomplete from "@mui/material/Autocomplete";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import {
  saveAsset,
  saveLoanDetails,
  updateAsset,
  updateLoanDetails,
} from "../../store/slices/applicationFormSlice";
import { assetTypes } from "../../assetTypes.json";
import generateArrayOfYears from "../../utils/generateArrayOfYears";
import { InputAdornment, Box } from "@mui/material";
import { fCurrency } from "src/utils/formatNumber";
import AssetActions from "./AssetActions";
import { assetConditionList } from "src/constants";

const Asset = ({ asset, loanDetails, index, isMaxAssetsReached }) => {
  const dispatch = useDispatch();
  const [assetExpanded, setAssetExpanded] = useState(
    index === 0 ? asset?._id : null
  );

  const {
    _id,
    assetValue = 0,
    ageOfAsset = "",
    assetType = "",
    make = "",
    model = "",
    typeOfSale = "",
    condition = "",
    supplierName = "",
    supplierABN = "",
    supplierAddress = "",
    supplierContactName = "",
    supplierPhone = "",
    supplierEmail = "",
  } = asset;

  const [allValues, setAllValue] = useState({
    assetValue: assetValue || "",
    ageOfAsset: ageOfAsset || "",
    assetType: assetType || "",
    make: make || "",
    model: model || "",
    typeOfSale: typeOfSale || "",
    condition: condition || "",
    supplierName: supplierName || "",
    supplierABN: supplierABN || "",
    supplierContactName: supplierContactName || "",
    supplierAddress: supplierAddress || "",
    supplierPhone: supplierPhone || "",
    supplierEmail: supplierEmail || "",
  });

  useEffect(() => {
    setAllValue({ ...asset });
  }, [asset]);

  const handle = {
    onChangeField: (field, value) => {
      if (field === "typeOfSale" && value === "Private sale") {
        setAllValue({ ...asset, [field]: value, condition: "Used" });
        return
      }
      setAllValue({ ...asset, [field]: value });
    },
    onBlurField: (field, value) => {
      if (field === "typeOfSale" && value === "Private sale") {
        dispatch(updateAsset({ _id: asset._id, [field]: value, condition: "Used" }));
      }
      dispatch(updateAsset({ _id: asset._id, [field]: value }));
    },
    handleAgeOfAsset: (event, newValue) => {
      const termInYears = loanDetails?.term / 12;
      const ageOfAssetMoment = moment(newValue, "YYYY");
      const endOfTermYear = moment().add(termInYears, "year");
      const ageOfAssetAtEnd = endOfTermYear.diff(ageOfAssetMoment, "years");
      if (newValue < moment().subtract(2, "years").format("YYYY")) {
        handle.handleBalloon(0);
      }
      dispatch(saveAsset({ ageOfAsset: newValue, ageOfAssetAtEnd }));
      dispatch(
        updateAsset({ _id: asset._id, ageOfAsset: newValue, ageOfAssetAtEnd })
      );
    },
    handleBalloon: (value) => {
      const updateBalloonPayload = {
        balloon: value,
      };
      dispatch(saveLoanDetails(updateBalloonPayload));
      dispatch(
        updateLoanDetails({ _id: loanDetails._id, ...updateBalloonPayload })
      );
    },
    handleAssetExpand: (id) => {
      setAssetExpanded((prev) => (prev === id ? null : id));
    },
  };

  const isConditionDisabled = allValues?.typeOfSale === "Private sale";

  return (
    <Accordion
      key={_id}
      expanded={assetExpanded === _id}
      onChange={() => handle.handleAssetExpand(_id)}
      disableGutters
      sx={{
        "&:before": { display: "none" },
        boxShadow: "none",
        border: "1px solid #eee",
        ...(index === 0 && {
          borderBottom: "3px solid #e0e0e0",
        }),
        ...(assetExpanded === asset?._id && {
          borderRadius: 2,
          border: "2px solid #eee",
        }),
      }}
    >
      <AccordionSummary
        sx={{
          "& .MuiAccordionSummary-content": {
            justifyContent: "space-between",
            alignItems: "center",
          },
          backgroundColor: assetExpanded === _id ? "#EFF4FF" : "#ffffff",
          marginBottom: "10px",
        }}
      >
        <Grid container alignItems="center">
          <Grid item xs={12} md={6} lg={4.5}>
            <Typography>{asset?.assetType || "-"}</Typography>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <Typography>
              {asset.assetValue ? fCurrency(asset.assetValue) : "$0.00"}
            </Typography>
          </Grid>
          <Grid
            item
            xs={12}
            md={6}
            lg={4.5}
            onClick={(e) => e.stopPropagation()}
          >
            <AssetActions index={index} asset={asset} isMaxAssetsReached={isMaxAssetsReached} />
          </Grid>
        </Grid>
      </AccordionSummary>
      <AccordionDetails style={{ paddingBottom: "40px" }}>
        <Grid container spacing={2}>
          <Grid item md={4} sm={12}>
            <TextField
              fullWidth
              id="assetValue"
              style={{ background: "#ffffff" }}
              type="number"
              name="assetValue"
              label="Asset value"
              error={!allValues?.assetValue}
              helperText={!allValues?.assetValue ? "Required" : ""}
              value={allValues?.assetValue}
              onChange={(event) =>
                handle.onChangeField("assetValue", event.target.value)
              }
              onBlur={(event) =>
                handle.onBlurField("assetValue", event.target.value)
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item md={8} sm={12}>
            <TextField
              fullWidth
              name="assetType"
              id="assetType"
              style={{ background: "#ffffff" }}
              select
              error={!allValues?.assetType}
              helperText={!allValues?.assetType ? "Required" : ""}
              value={allValues?.assetType || ""}
              label="Asset type"
              onChange={(event) =>
                handle.onChangeField("assetType", event.target.value)
              }
              onBlur={(event) =>
                handle.onBlurField("assetType", event.target.value)
              }
            >
              {assetTypes.map((asset, i) => (
                <MenuItem value={asset.label} key={asset.label}>
                  {asset.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item md={4} sm={12}>
            <TextField
              fullWidth
              id="make"
              type="text"
              name="make"
              label="Make"
              value={allValues?.make || ""}
              error={!allValues?.make}
              helperText={!allValues?.make ? "Required" : ""}
              onChange={(event) =>
                handle.onChangeField("make", event.target.value)
              }
              onBlur={(event) => handle.onBlurField("make", event.target.value)}
              inputProps={{
                autoComplete: "none",
                disableUnderline: true,
              }}
            />
          </Grid>

          <Grid item md={8} sm={12}>
            <TextField
              fullWidth
              id="model"
              type="text"
              name="model"
              label="Model"
              value={allValues?.model || ""}
              error={!allValues?.model}
              helperText={!allValues?.model ? "Required" : ""}
              onChange={(event) =>
                handle.onChangeField("model", event.target.value)
              }
              onBlur={(event) =>
                handle.onBlurField("model", event.target.value)
              }
              inputProps={{
                disableUnderline: true,
                autoComplete: "none",
              }}
            />
          </Grid>

          <Grid item md={3} sm={12}>
            <TextField
              fullWidth
              id="condition"
              style={{ background: "#ffffff" }}
              select
              value={allValues?.condition || ""}
              error={!allValues?.condition}
              disabled={isConditionDisabled}
              helperText={!allValues?.condition ? "Required" : ""}
              label="Condition"
              onChange={(event) =>
                handle.onChangeField("condition", event.target.value)
              }
              onBlur={(event) =>
                handle.onBlurField("condition", event.target.value)
              }
            >
              {assetConditionList.map((asset) => (
                <MenuItem value={asset} key={asset}>
                  {asset}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item md={3} sm={12}>
            <Autocomplete
              id="ageOfAsset"
              value={allValues?.ageOfAsset?.toString() || ""}
              options={generateArrayOfYears()}
              onChange={handle.handleAgeOfAsset}
              getOptionLabel={(option) => option || ""}
              renderInput={(params) => (
                <TextField {...params} label="Year of manufacture" />
              )}
            />
          </Grid>
          <Grid item md={6} sm={12}>
            <TextField
              fullWidth
              id="typeOfSale"
              style={{ background: "#ffffff" }}
              select
              value={allValues?.typeOfSale || ""}
              error={!allValues?.typeOfSale}
              helperText={!allValues?.typeOfSale ? "Required" : ""}
              label="Type of sale"
              onChange={(event) =>
                handle.onChangeField("typeOfSale", event.target.value)
              }
              onBlur={(event) =>
                handle.onBlurField("typeOfSale", event.target.value)
              }
            >
              {[
                { label: "Accredited dealer", default: true },
                { label: "Private sale" },
                { label: "Mid-term refinance" },
                { label: "Balloon | Residual Refinance" },
                { label: "Sale & buyback | Equity" },
              ].map((asset) => (
                <MenuItem
                  default={asset.default}
                  value={asset.label}
                  key={asset.label}
                >
                  {asset.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
        <Box
          sx={{
            backgroundColor: "#F8F9FA",
            borderRadius: 2,
            padding: "20px 16px",
            border: "1px solid #E9ECEF",
            marginTop: "8px",
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              color: "#495057",
              marginBottom: "20px",
              fontSize: "0.95rem",
              letterSpacing: "0.01em",
            }}
          >
            Supplier Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item md={7} sm={12}>
              <TextField
                fullWidth
                id="supplierName"
                name="supplierName"
                style={{ background: "#ffffff" }}
                label="Supplier name"
                value={allValues?.supplierName || ""}
                onChange={(event) =>
                  handle.onChangeField("supplierName", event.target.value)
                }
                onBlur={(event) =>
                  handle.onBlurField("supplierName", event.target.value)
                }
              />
            </Grid>

            <Grid item md={5} sm={12}>
              <TextField
                fullWidth
                id="supplierABN"
                name="supplierABN"
                style={{ background: "#ffffff" }}
                value={allValues?.supplierABN || ""}
                label="Supplier ABN"
                onChange={(event) =>
                  handle.onChangeField("supplierABN", event.target.value)
                }
                onBlur={(event) =>
                  handle.onBlurField("supplierABN", event.target.value)
                }
              />
            </Grid>

            <Grid item md={12} sm={12}>
              <TextField
                fullWidth
                id="supplierAddress"
                type="text"
                name="supplierAddress"
                label="Supplier Address"
                style={{ background: "#ffffff" }}
                value={allValues?.supplierAddress || ""}
                onChange={(event) =>
                  handle.onChangeField("supplierAddress", event.target.value)
                }
                onBlur={(event) =>
                  handle.onBlurField("supplierAddress", event.target.value)
                }
                InputProps={{ disableUnderline: true }}
              />
            </Grid>
            <Grid item md={4} sm={12}>
              <TextField
                fullWidth
                id="supplierContactName"
                type="text"
                name="supplierContactName"
                label="Supplier Contact Name"
                style={{ background: "#ffffff" }}
                value={allValues?.supplierContactName || ""}
                onChange={(event) =>
                  handle.onChangeField(
                    "supplierContactName",
                    event.target.value
                  )
                }
                onBlur={(event) =>
                  handle.onBlurField("supplierContactName", event.target.value)
                }
                InputProps={{ disableUnderline: true }}
              />
            </Grid>
            <Grid item md={4.5} sm={12}>
              <TextField
                fullWidth
                id="supplierEmail"
                type="text"
                name="supplierEmail"
                label="Supplier Email"
                style={{ background: "#ffffff" }}
                value={allValues?.supplierEmail || ""}
                onChange={(event) =>
                  handle.onChangeField("supplierEmail", event.target.value)
                }
                onBlur={(event) =>
                  handle.onBlurField("supplierEmail", event.target.value)
                }
                InputProps={{ disableUnderline: true }}
              />
            </Grid>
            <Grid item md={3.5} sm={12}>
              <TextField
                fullWidth
                id="supplierPhone"
                type="text"
                name="supplierPhone"
                label="Supplier Phone Number"
                style={{ background: "#ffffff" }}
                value={allValues?.supplierPhone || ""}
                onChange={(event) =>
                  handle.onChangeField("supplierPhone", event.target.value)
                }
                onBlur={(event) =>
                  handle.onBlurField("supplierPhone", event.target.value)
                }
                InputProps={{ disableUnderline: true }}
              />
            </Grid>
          </Grid>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default Asset;
